import { useContext } from 'react'
import { IconContext } from 'react-icons'
import {
  MdAutoGraph,
  MdDeleteOutline,
  MdPostAdd,
  MdSettings,
} from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { SidebarContext } from '../../../lib/Context'
import { Spend } from '../../../lib/Types'
import PaymentGuard from '../PaymentGuard'
import { useBudget } from '../lib/Hooks/Budget'
import { useDeleteSpend, useUpdateSpend } from '../lib/Hooks/Spends'
import SpendConfiguration from './SpendConfiguration'
import SpendDevelopment from './SpendDevelopment'

interface Props {
  spend: Spend
  isChild?: boolean
  toggleAddSpend?: () => void
  toggleChildren?: () => void
}

const TitleColumn = ({
  spend,
  isChild,
  toggleAddSpend,
  toggleChildren,
}: Props) => {
  const { t } = useTranslation(['budget'])
  const { data: budget } = useBudget()
  const { showSidebar, isSidebarOpen } = useContext(SidebarContext)
  const { mutate: updateSpend } = useUpdateSpend(budget!)
  const { mutate: deleteSpend } = useDeleteSpend(budget!)

  const hasChildren = () => {
    return spend.children && spend.children.length > 0
  }

  return (
    <PaymentGuard sheet={budget!}>
      {(guard) => (
        <div className='flex items-center'>
          <div
            className={`flex flex-col grow ${hasChildren() ? 'cursor-pointer' : ''}`}
            onClick={toggleChildren}
          >
            <p className={`${isChild ? 'ml-5' : ''}`}>{spend.title}</p>
            {spend.description && (
              <p className={`italic ${isChild ? 'ml-5' : ''}`}>
                {spend.description}
              </p>
            )}
          </div>

          {!hasChildren() && (
            <MdAutoGraph
              size='1.15rem'
              className='mr-2 cursor-pointer'
              title={t('budget:titleColumn.viewDevelopment')}
              onClick={guard(() => {
                isSidebarOpen
                  ? showSidebar(null)
                  : showSidebar(<SpendDevelopment spend={spend} />)
              })}
            />
          )}
          {toggleAddSpend && !isChild && (
            <IconContext.Provider
              value={{ size: '1.25em', className: 'mr-1' }}
            >
              <MdPostAdd
                size='1.15rem'
                onClick={guard(toggleAddSpend)}
                className='mr-2 cursor-pointer'
                title={t('budget:titleColumn.addSpend')}
              />
            </IconContext.Provider>
          )}
          <MdSettings
            size='1.15rem'
            className='mr-2 cursor-pointer'
            title={t('budget:titleColumn.edit')}
            onClick={guard(() => {
              isSidebarOpen
                ? showSidebar(null)
                : showSidebar(
                  <SpendConfiguration
                    saveSpend={async (spend) => {
                      updateSpend(spend)
                      showSidebar(null)
                    }}
                    spend={spend}
                  />,
                )
            })}
          />
          <IconContext.Provider value={{ size: '1.25em' }}>
            <MdDeleteOutline
              size='1.15rem'
              onClick={guard(async () => {
                if (window.confirm(t('budget:titleColumn.confirmDelete'))) {
                  deleteSpend(spend)
                }
              })}
              className='cursor-pointer'
              title={t('budget:titleColumn.delete')}
            />
          </IconContext.Provider>
        </div>
      )}
    </PaymentGuard>
  )
}

export default TitleColumn
